import React from 'react';
import { Box, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { categoryColor } from './indicatorConfig';
import { formatPercent, formatScore } from './formatters';
import { INDICATOR_SCORE_MAX } from './scoreScale';

const COVERAGE_COLOR = '#f59e0b';

const InsightCard = ({ color, icon: Icon, headline, children, onDismiss, dismissLabel, testId }) => (
  <Paper
    variant="outlined"
    data-testid={testId}
    sx={{
      p: 1.5,
      borderLeft: `4px solid ${color}`,
      display: 'flex',
      gap: 1.5,
      alignItems: 'flex-start',
      height: '100%',
      width: '100%',
    }}
  >
    <Icon sx={{ color, mt: 0.25, flexShrink: 0 }} />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={700}>
        {headline}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div">
        {children}
      </Typography>
    </Box>
    {onDismiss && (
      <Tooltip title={dismissLabel}>
        <IconButton size="small" onClick={onDismiss} aria-label={dismissLabel} sx={{ flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Paper>
);

const InsightCards = ({ driver, coverage, entityNounPlural, onDismissCoverage }) => {
  const cards = [];

  if (driver) {
    const color = categoryColor(driver.category);
    cards.push(
      <InsightCard
        key="driver"
        color={color}
        icon={TrendingUpIcon}
        headline={`${driver.label} is separating the field`}
        testId="insight-driver"
      >
        {formatScore(driver.spread)} points between {driver.topName} and {driver.bottomName} — the
        widest spread of any indicator. It carries {formatPercent(driver.weight, 0)} of weight and
        contributes {formatScore(driver.meanContribution)} points on average.
        {driver.runnerUp && driver.runnerUp.spread > 0 && (
          <> Next widest is {driver.runnerUp.label} at {formatScore(driver.runnerUp.spread)}.</>
        )}
        {driver.flat.length > 0 && (
          <>
            {' '}
            {driver.flat.length === 1 ? 'One weighted indicator' : `${driver.flat.length} weighted indicators`}{' '}
            barely differentiate: {driver.flat.map((item) => item.label).join(', ')}.
          </>
        )}
      </InsightCard>
    );
  }

  if (coverage) {
    const entityNoun = entityNounPlural.replace(/s$/, '');
    const labels = coverage.fullyMissing.map((item) => item.label).join(', ');
    const headline = coverage.missingWeight > 0
      ? `${formatPercent(coverage.missingWeight, 0)} of weight sits on indicators with no data`
      : 'Some indicators are missing data';

    cards.push(
      <InsightCard
        key="coverage"
        color={COVERAGE_COLOR}
        icon={WarningAmberIcon}
        headline={headline}
        onDismiss={onDismissCoverage}
        dismissLabel="Dismiss data coverage note"
        testId="insight-coverage"
      >
        {coverage.fullyMissing.length > 0 && (
          <>
            {labels} {coverage.fullyMissing.length === 1 ? 'is' : 'are'} missing for every{' '}
            {entityNoun}. Every {entityNoun} loses the same{' '}
            {formatScore(coverage.missingWeight * INDICATOR_SCORE_MAX)} points, so the ranking order holds.
          </>
        )}
        {coverage.partiallyMissing.map((item) => (
          <Box component="span" sx={{ display: 'block', mt: 0.5 }} key={item.id}>
            {item.missingNames.join(', ')} {item.missingCount === 1 ? 'has' : 'have'} no{' '}
            {item.label.toLowerCase()} value and forfeit {formatPercent(item.weight, 0)} of weight —
            that one does shift rank order.
          </Box>
        ))}
        <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
          Switch scoring to Renormalized to redistribute missing weight instead.
        </Box>
      </InsightCard>
    );
  }

  if (!cards.length) return null;

  const columns = cards.length === 1 ? 12 : 6;

  return (
    <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="stretch">
      {cards.map((card) => (
        <Grid item xs={12} md={columns} key={card.key} sx={{ display: 'flex' }}>
          {card}
        </Grid>
      ))}
    </Grid>
  );
};

export default InsightCards;
